import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "../lib/utils"
import { Spinner } from "./spinner"

const buttonVariants = cva(
  "group/button relative inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // ProERP-Kompat (ADR 0020): `primary`/`danger`/`md` als Aliasse der
        // Bestands-API; Default ohne Variant = Outline-Look (alte `secondary`).
        default: "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        primary: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary: "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive: "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        danger: "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        md: "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5 touch:after:absolute touch:after:-inset-1 touch:after:content-['']",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        // Icon-Größen sind auf der Maus bewusst kompakt (Dichte) — auf Touch (iPad)
        // wächst die Trefferfläche über ein unsichtbares `::after` (kein Layout-
        // Sprung), da 24–32px unter dem 44px-Mindestmaß liegen (ADR-los, iPad-Opt. 2026-07-13).
        icon: "size-8 touch:after:absolute touch:after:-inset-1 touch:after:content-['']",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3 touch:after:absolute touch:after:-inset-1.5 touch:after:content-['']",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg touch:after:absolute touch:after:-inset-1 touch:after:content-['']",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * Sperre nach einem Klick auf einen Aktions-Button — die übliche Doppelklick-Schwelle.
 * Deckt auch den schnellen Server ab: Ist die Aktion nach 50 ms fertig, wäre der Button
 * während der Schließ-Animation des Dialogs sonst wieder frei für den zweiten Klick.
 */
const DOUBLE_SUBMIT_LOCK_MS = 500

// Angepasst: forwardRef — unter React 18 reichen Radix-Trigger (DropdownMenu,
// Dialog, Popover) ihren Ref über asChild hierher durch.
const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
      /** ProERP-Kompat: zeigt einen Spinner und deaktiviert den Button. */
      isLoading?: boolean
    }
>(function Button(
  {
    className,
    variant = "default",
    size = "default",
    asChild = false,
    isLoading,
    disabled,
    children,
    ...props
  },
  ref
) {
  const Comp = asChild ? Slot.Root : "button"
  const { onClick } = props
  const lockedUntil = React.useRef(0)
  const isRunning = React.useRef(false)

  /*
   * Doppelklick-Schutz für Aktions-Buttons (wer `isLoading` angibt, löst eine
   * Aktion aus): `isLoading={mutation.isPending}` sperrt erst nach dem nächsten
   * Rendern — ein Doppelklick buchte Lagerbestand doppelt und legte Belege
   * doppelt an (Qualitäts-Audit 2026-09-28). Nach jedem Klick ist der Button kurz
   * gesperrt; liefert der Handler ein Promise, zusätzlich bis es erledigt ist.
   * Buttons ohne `isLoading` (Umschalter, Zähler) bleiben frei.
   */
  const guardedOnClick: React.MouseEventHandler<HTMLButtonElement> | undefined =
    isLoading === undefined || !onClick
      ? onClick
      : (event) => {
          const now = Date.now()
          if (isRunning.current || now < lockedUntil.current) {
            event.preventDefault()
            return
          }
          lockedUntil.current = now + DOUBLE_SUBMIT_LOCK_MS
          const result: unknown = onClick(event)
          if (result instanceof Promise) {
            isRunning.current = true
            result.finally(() => {
              isRunning.current = false
            })
          }
        }

  return (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled || isLoading}
      {...props}
      onClick={guardedOnClick}
    >
      {isLoading ? (
        <>
          <Spinner aria-hidden />
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  )
})

export type ButtonProps = React.ComponentProps<typeof Button>

export { Button, buttonVariants }
